import { useState } from 'react';
import type { SavedViewEntityType, SavedViewResponse } from '@orion/contracts';
import { useCreateSavedView, useDeleteSavedView, useSavedViewsQuery } from './api.js';

/** docs section 22 "Filters / Saved Views" — private/team/everyone views
 * over the one real filter each list page has today (its search box). The
 * filter builder itself is design-canvas-only for now; this wires up what
 * the backend actually supports rather than mocking columns/sort it can't
 * yet apply. */
export function SavedViewsBar({
  entityType,
  q,
  onApplyQuery,
}: {
  entityType: SavedViewEntityType;
  q: string;
  onApplyQuery: (q: string) => void;
}) {
  const { data: views } = useSavedViewsQuery(entityType);
  const createView = useCreateSavedView(entityType);
  const deleteView = useDeleteSavedView(entityType);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState('');

  const apply = (view: SavedViewResponse | null) => {
    setActiveId(view?.id ?? null);
    const viewQ = view?.filters.q;
    onApplyQuery(typeof viewQ === 'string' ? viewQ : '');
  };

  const save = async () => {
    if (!name.trim()) return;
    const created = await createView.mutateAsync({
      entityType,
      name: name.trim(),
      filters: { q },
      sort: [],
      columns: [],
      visibility: 'private',
      isDefault: false,
      showInSidebar: false,
    });
    setActiveId(created.id);
    setName('');
    setIsSaving(false);
  };

  const remove = async (view: SavedViewResponse) => {
    await deleteView.mutateAsync(view.id);
    if (activeId === view.id) apply(null);
  };

  const pillClass = (active: boolean) =>
    `inline-flex h-7 items-center gap-1 rounded-full px-3 text-xs font-medium ${
      active ? 'bg-primary-soft text-primary-soft-text' : 'bg-surface-subtle text-text-secondary hover:bg-surface'
    }`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" onClick={() => apply(null)} className={pillClass(activeId === null)}>
        All
      </button>
      {views?.map((view) => (
        <span key={view.id} className={pillClass(activeId === view.id)}>
          <button type="button" onClick={() => apply(view)}>
            {view.name}
          </button>
          <button type="button" onClick={() => remove(view)} aria-label={`Delete view "${view.name}"`} className="text-text-muted hover:text-danger">
            ×
          </button>
        </span>
      ))}

      {isSaving ? (
        <span className="flex items-center gap-1.5">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            placeholder="View name"
            className="h-7 rounded-control border border-border-strong bg-surface px-2 text-xs"
          />
          <button type="button" onClick={save} className="text-xs font-semibold text-primary">
            Save
          </button>
          <button type="button" onClick={() => setIsSaving(false)} className="text-xs text-text-muted">
            Cancel
          </button>
        </span>
      ) : (
        <button type="button" onClick={() => setIsSaving(true)} className="text-xs font-semibold text-primary hover:underline">
          + Save current search
        </button>
      )}
    </div>
  );
}
